import { useState } from 'react';
import Modal from '../Modal';
import workflowService from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';

interface Props {
  caseId: string;
  /** event_trigger sent to /workflow/cases/{id}/transition */
  event: string;
  /** Modal title — describes the action (e.g. "Reject contract") */
  title: string;
  /** One-line description shown under the title to clarify what happens
   *  next. Helps the staff distinguish between similar actions. */
  description: string;
  /** Text on the confirmation button */
  submitLabel: string;
  /** Visual emphasis. `danger` is used for irreversible terminals (Reject);
   *  `primary` for everything else. */
  submitVariant: 'primary' | 'danger';
  onClose: () => void;
  onSubmitted: () => void;
}

const MIN_REASON = 5;
const MAX_REASON = 1000;

/**
 * Single-textarea modal for transitions whose `requires_reason=true` is the
 * only input requirement (no client-visible message). Used for:
 *   - manager:return_to_agent  (manager bounces internally to the agent)
 *   - manager:reject           (terminal rejection)
 *
 * Privacy: the text entered here goes ONLY into `state_transitions_log.notes`
 * (internal staff thread). It is NEVER returned by the timeline endpoint to
 * client viewers. If a use case ever needs a client-visible message AND a
 * reason, that uses the ReturnToClientModal instead.
 */
export default function SimpleReasonModal({
  caseId,
  event,
  title,
  description,
  submitLabel,
  submitVariant,
  onClose,
  onSubmitted,
}: Readonly<Props>): JSX.Element {
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON) {
      setError(`Reason must be at least ${MIN_REASON} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      await workflowService.applyTransition(caseId, {
        event_trigger: event,
        notes: trimmed,
      });
      onSubmitted();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not apply this action.'));
    } finally {
      setSubmitting(false);
    }
  }

  const submitButtonStyle =
    submitVariant === 'danger'
      ? { background: colors.danger, color: '#FFFFFF' }
      : { background: colors.navy, color: '#FFFFFF' };

  return (
    <Modal open onClose={onClose} busy={submitting} maxWidth="lg">
      <h2
        id="simple-reason-modal-title"
        className="text-lg font-semibold mb-1"
        style={{ color: colors.ink }}
      >
        {title}
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        {description}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block text-sm">
          <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
            Internal reason{' '}
            <span className="font-normal" style={{ color: colors.mutedSoft }}>
              ({MIN_REASON}–{MAX_REASON} chars; staff-only, not visible to the member)
            </span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
            minLength={MIN_REASON}
            maxLength={MAX_REASON}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
            placeholder="Explain why for the staff audit log."
          />
        </label>

        {error && (
          <div
            className="rounded-lg p-3 text-sm"
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
            }}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              ...submitButtonStyle,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            {submitting ? 'Submitting…' : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
